import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { Icon, BasicTooltip, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { AllowedSendersModal } from './AllowedSendersModal';
import useStyles from './billIntakeAliasBannerStyles';

/**
 * The company's bill-intake email address, pinned at the top of the Bills page (LB-5769) — the one
 * surface where a PM discovers where to send bills. Copy is one click; bills sent to this address
 * route to the company automatically (Gate 0 tag routing).
 *
 * Reading never provisions: email intake is a paid feature, so a company without a provisioned
 * alias simply has none and the banner renders nothing. It also renders nothing if the lookup
 * fails — the Bills page must not degrade because of it, which is why this uses the silent async:
 * a toast would report an error about something the user never asked for.
 * PM portal only; this must never appear on a tenant-facing surface.
 */
export const BillIntakeAliasBanner = observer(() => {
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    alias = _useState2[0],
    setAlias = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSendersOpen = _useState4[0],
    setIsSendersOpen = _useState4[1];
  useEffect(() => {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company);
    // localStorage holds the STRING 'null' when no company is selected, so a truthiness check
    // alone would call the API with companyId='null'.
    if (!companyId || companyId === 'null' || companyId === 'undefined') return undefined;
    let active = true;
    const fetchAlias = async () => {
      const resp = await store.company.getBillIntakeAliasSilentAsync.run({
        companyId
      });
      if (active) setAlias((resp === null || resp === void 0 ? void 0 : resp.response) || null);
    };
    fetchAlias();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleCopy = () => {
    navigator.clipboard.writeText(alias.address).then(() => {
      toast('success', {
        body: ['Successfully copied to clipboard!']
      });
    }).catch(error => {
      toast('error', {
        body: ['Error copying to clipboard', error]
      });
    });
  };
  if (!(alias !== null && alias !== void 0 && alias.address)) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.banner
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "mail"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.bannerText
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.address
  }, alias.address), /*#__PURE__*/React.createElement("span", {
    className: classes.hint
  }, "Email bills to this address and they are routed to your company automatically.")), /*#__PURE__*/React.createElement(BasicTooltip, {
    title: "Copy to clipboard",
    placement: "top"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    onClick: handleCopy
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "document-file-copy"
  }))), /*#__PURE__*/React.createElement(AllowedSendersModal, {
    isOpen: isSendersOpen,
    handleOpen: () => setIsSendersOpen(true),
    handleClose: () => setIsSendersOpen(false),
    trigger: /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      onClick: () => setIsSendersOpen(true)
    }, "Manage allowed senders")
  }));
});